<script setup lang="ts">
	import type { UiConfigType } from './card.vue'
	import type { ListContext } from './list-item.vue'

	const props = defineProps<{
		totalSum?: string | number
		loading?: boolean
		summary?: ListContext | null
		hideTerms?: boolean
		btnText?: string
		wrapperUi?: Partial<UiConfigType>
		cardUi?: Partial<UiConfigType>
	}>()
	const emit = defineEmits<{
		(arg: 'update:loading', param: boolean): void
		(arg: 'proceed', param: { terms_accepted: boolean }): void
		(arg: 'openTerms'): void
	}>()
	const terms = shallowRef('')
	const termsNotAccepted = shallowRef(false)
	const { internationalNoFormat } = useCurrency()
	const proceed = () => {
		termsNotAccepted.value = !terms.value
		if (terms.value || props.hideTerms) {
			emit('proceed', { terms_accepted: termsNotAccepted.value })
			emit('update:loading', true)
		}
	}

	watch(terms, (v: string) => {
		termsNotAccepted.value = !v
	})
</script>

<template>
	<shared-card
		:ui="{
			container:
				'sm:bg-white flex flex-col items-center py-10 sm:px-5 md:px-20 max-w-[800px] mx-auto md:min-h-[754px]',
			...(wrapperUi || {})
		}"
	>
		<slot name="append" />
		<div class="w-full border border-primary-50 bg-primary-25 md:p-[24px]">
			<shared-card
				:ui="{
					container: 'py-10 md:px-8 px-3 h-full',
					...(cardUi || {})
				}"
			>
				<shared-list-item :lists="summary || {}" />
			</shared-card>
		</div>
		<br />
		<div
			class="mb-5 flex w-full items-center justify-center rounded-[4px] bg-success-50 p-3 font-semibold sm:text-[24px]"
		>
			{{ internationalNoFormat(totalSum || 0, '₦') }}
		</div>

		<div class="flex w-full flex-col items-center justify-center gap-2 text-gray-600">
			<div v-if="!hideTerms" class="flex items-center pt-3">
				<input
					id="terms"
					v-model="terms"
					type="checkbox"
					class="h-[20px] w-[20px] rounded-[4px] checked:border-primary-300 checked:bg-primary-2"
					:class="{
						'border-red-400': termsNotAccepted,
						'border-gray-400': !termsNotAccepted
					}"
				/>
				<label
					for="terms"
					class="ml-2 text-sm font-light"
					:class="{
						'text-red-500': termsNotAccepted,
						'text-[#475467]': !termsNotAccepted
					}"
				>
					I acknowledge and agree to the
					<span class="cursor-pointer pl-1 text-sm text-primary-500" @click="emit('openTerms')">
						Terms & Conditions
					</span>
				</label>
			</div>

			<p class="mt-6" />
			<slot name="btn" :amount="totalSum" :terms-agreed="!terms">
				<a-button
					:loading="loading"
					class="default-btn w-full justify-center bg-primary-2 !font-medium"
					:class="{ 'cursor-not-allowed': !terms }"
					data-testid="continue-payment"
					:disabled="!terms && !hideTerms"
					@click="proceed"
				>
					{{ btnText || 'Continue to payment' }}
				</a-button>
			</slot>
		</div>
	</shared-card>
</template>
